/**
 * Category Products Page
 * ======================
 * Display products for a specific category including subcategories.
 */

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Package, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import { useProducts } from '@/hooks/useProducts'
import { useCategory, useCategories } from '@/hooks/useCategories'
import { getProductThumbnailUrl } from '@/utils/images'
import type { ProductSearchParams } from '@/types'

export default function CategoryProducts() {
  const { categoryId } = useParams<{ categoryId: string }>()
  const navigate = useNavigate()
  const categoryIdNum = categoryId ? parseInt(categoryId, 10) : null

  const [searchParams, setSearchParams] = useState<ProductSearchParams>({
    page: 1,
    limit: 20,
    category_id: categoryIdNum,
    include_subcategories: true,
  })

  // Fetch category details
  const { data: categoryData, isLoading: categoryLoading } = useCategory(categoryIdNum ?? 0)
  const category = categoryData?.data

  // Fetch all categories for building path
  const { data: categoriesData } = useCategories()
  const categories = categoriesData?.data ?? []

  // Fetch products in this category (including subcategories)
  const { data, isLoading, error } = useProducts(searchParams)
  const products = data?.data ?? []
  const meta = data?.meta

  // Build category path (e.g., "Electronics > Computers > Laptops")
  const getCategoryPath = (): string => {
    if (!categoryIdNum) return ''

    const path: string[] = []
    let currentId: number | null = categoryIdNum

    // Walk up the parent chain (max 10 levels to prevent infinite loops)
    for (let i = 0; i < 10 && currentId; i++) {
      const cat = categories.find(c => c.id === currentId)
      if (!cat) break
      path.unshift(cat.name)
      currentId = cat.parent_id
    }

    return path.join(' > ')
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }))
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading products</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/categories')}
            title="Back to categories"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {categoryLoading ? 'Loading...' : category?.name ?? 'Category Products'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {getCategoryPath()}
              {meta && ` • ${meta.total} product${meta.total !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <Link to="/products/new">
          <Button icon={<Plus size={16} />}>Add Product</Button>
        </Link>
      </div>

      {/* Info banner */}
      <Card>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing all products in <strong>{category?.name}</strong> and its subcategories.
          </p>
        </CardContent>
      </Card>

      {/* Products list */}
      <Card>
        <CardHeader title="Products" />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID / SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Qty
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Package className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
                    <p className="text-gray-500 dark:text-gray-400">No products in this category</p>
                    <Link
                      to="/products/new"
                      className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block"
                    >
                      Add a product
                    </Link>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/products/${product.product_id}`}
                        className="flex items-center gap-3"
                      >
                        {getProductThumbnailUrl(product) ? (
                          <img
                            src={getProductThumbnailUrl(product)!}
                            alt={product.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <Package className="text-gray-400" size={20} />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {product.name}
                          </p>
                          {product.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {product.description}
                            </p>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-mono text-gray-900 dark:text-white">
                        {product.product_id}
                      </p>
                      {product.sku && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{product.sku}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {product.category?.name ?? '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {product.location?.name ?? '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          product.quantity === 0
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                            : product.quantity < 5
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                        }`}
                      >
                        {product.quantity}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
